import { ChangeEventHandler, FocusEventHandler, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { isWebEDIFormError, WebEDIAnnotation } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { WithRef } from 'support/types/withRef';

import { Textarea } from '../Input/Textarea/Textarea';

type SchemableFormTextareaProps = Omit<Parameters<typeof Textarea>[0], 'errors' | 'hasErrors'> & {
  error: WebEDIAnnotation | undefined;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
};
export const SchemableFormTextarea = ({
  error,
  onBlur,
  onShowFieldErrorModal,
  description,
  variant,
  valueDescription,
  fieldState,
  onChange,
  name,
  value,
  ref,
  ...otherProps
}: WithRef<SchemableFormTextareaProps, HTMLTextAreaElement>) => {
  const { t } = useTranslation();
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const { NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext();
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();
  const onTextareaFocus = useCallback(() => {
    setShowNavigableItemPopover(true);
  }, [setShowNavigableItemPopover]);

  const onTextareaBlur: FocusEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      onBlur?.(e);
      setShowNavigableItemPopover(false);
    },
    [onBlur, setShowNavigableItemPopover],
  );

  const handleOnChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback(
    (event) => {
      onChange?.(event);
    },
    [onChange],
  );

  useEffect(() => {
    if (error && showNavigableItemPopover) {
      onShowFieldErrorModal?.(true);
    }
  }, [error, onShowFieldErrorModal, showNavigableItemPopover]);

  if (!name) {
    return null;
  }

  const isLargeVariant = variant === 'large';
  const inlineErrors =
    isLargeVariant && isWebEDIFormError(error) ? { message: error.description, type: error.type } : undefined;

  return (
    <div>
      <Textarea
        {...otherProps}
        value={value ?? ''}
        placeholder={
          !value && isWebEDIFormError(error) && error.type === 'required'
            ? t('common:table.errors.requiredField')
            : undefined
        }
        onChange={handleOnChange}
        ref={ref}
        wrapperRef={isLargeVariant ? undefined : refs.setReference}
        description={isLargeVariant ? description : undefined}
        errors={inlineErrors}
        fieldState={fieldState}
        onFocus={isLargeVariant ? undefined : onTextareaFocus}
        onBlur={isLargeVariant ? onBlur : onTextareaBlur}
        name={name}
        data-field={name}
        data-1p-ignore={true}
        {...(isLargeVariant ? { variant, valueDescription } : { variant: 'default' })}
      />

      {!isLargeVariant && showNavigableItemPopover && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent
          viewMode={false}
          annotation={error}
          ref={refs.setFloating}
          style={floatingStyles}
          isValidating={isValidating}
          fieldDescription={description}
          isPendingValidation={fieldState === 'pending-validation'}
        />
      )}
    </div>
  );
};
